import { Heading } from '../../../../../../mads';
import { HtmlContent } from '../../../../../../shared/components/HtmlContent';
import { useI18n } from '../../../../../../shared/I18n';
import { ScreenDisplay } from '../../../../context/ViewJobScreenDisplay';
import { HiddenPreview } from '../../HiddenPreview/HiddenPreview';

import { colors } from '@indeed/ifl-tokens';
import React, { useCallback, useEffect, useState } from 'react';
import { AccessibilityInfo, InteractionManager, Platform, useWindowDimensions } from 'react-native';

export type JobDescriptionProps = {
    /**
     * The Job Description which can contain HTML elements.
     */
    jobDescription: string;
};

export const SIMPLE_JD_HTML_CLASS = 'simple-job-description-html';

// Backend rich-formatting (`agg`) emits some content (e.g. items in 2-column tables)
// as `<h1>`–`<h6>` with the `jobSectionHeader` class stripped by Jsoup. Without
// this rule, headings fall through to WebView UA default sizes and render much
// larger than the surrounding body text. Other block tags (`p`, `ul`, `li`,
// `dl`, `dd`) already inherit the body 1rem on native, but we list them here
// to defensively absorb any inline `font-size` attribute the backend might
// emit and to match web MR 2716 (RN-3699 / JSF-279) selector-for-selector.
export const SIMPLE_JD_INJECTED_STYLES = `
    .${SIMPLE_JD_HTML_CLASS} p,
    .${SIMPLE_JD_HTML_CLASS} ul,
    .${SIMPLE_JD_HTML_CLASS} li,
    .${SIMPLE_JD_HTML_CLASS} dl,
    .${SIMPLE_JD_HTML_CLASS} dd,
    .${SIMPLE_JD_HTML_CLASS} h1,
    .${SIMPLE_JD_HTML_CLASS} h2,
    .${SIMPLE_JD_HTML_CLASS} h3,
    .${SIMPLE_JD_HTML_CLASS} h4,
    .${SIMPLE_JD_HTML_CLASS} h5,
    .${SIMPLE_JD_HTML_CLASS} h6 {
        font-size: inherit;
    }
`;

export const SimpleJobDescription = (props: JobDescriptionProps) => {
    const { gettext } = useI18n();
    const [hidden, setHidden] = useState(true);
    const [shouldFocusContent, setShouldFocusContent] = useState(false);
    const [height, setHeight] = useState<number | undefined>(undefined);
    const { height: windowHeight } = useWindowDimensions();

    // Use fixed height on Android when TalkBack is enabled to avoid screen reader focus issues.
    useEffect(() => {
        if (Platform.OS !== 'android') {
            return;
        }
        AccessibilityInfo.isScreenReaderEnabled()
            .then((isScreenReaderEnabled) => {
                if (isScreenReaderEnabled) {
                    setHeight(
                        // 40% of screen, max 500px
                        Math.min(Math.round(windowHeight * 0.4), 500),
                    );
                }
            })
            .catch((error) => {
                console.error('Error checking TalkBack status:', error);
            });
    }, [windowHeight]);

    const handleHiddenChange = useCallback((newHidden: boolean) => {
        setHidden(newHidden);

        // Reset focus flag every time toggle happens
        // so each reveal can trigger focus again
        setShouldFocusContent(false);

        if (!newHidden) {
            // Wait for interactions to complete, then ensure content is rendered
            void InteractionManager.runAfterInteractions(() => {
                // Small additional delay if needed to ensure DOM updates
                setTimeout(() => {
                    setShouldFocusContent(true);
                }, 50);
            });
        }
    }, []);

    const heading = gettext('Full job description');

    return (
        <>
            <Heading
                level={4}
                id="jobDescriptionTitleHeading"
                sx={{
                    color: colors.neutral['1000'],
                    marginBottom: 8,
                    marginTop: 24,
                }}
            >
                {heading}
            </Heading>
            <ScreenDisplay.DesktopOnly>
                <HtmlContent
                    className={SIMPLE_JD_HTML_CLASS}
                    injectedStyles={SIMPLE_JD_INJECTED_STYLES}
                >
                    {props.jobDescription}
                </HtmlContent>
            </ScreenDisplay.DesktopOnly>
            <ScreenDisplay.MobileOnly>
                <HiddenPreview height={100} hidden={hidden} onHiddenChange={handleHiddenChange}>
                    <HtmlContent
                        className={SIMPLE_JD_HTML_CLASS}
                        injectedStyles={SIMPLE_JD_INJECTED_STYLES}
                        shouldFocusContent={shouldFocusContent}
                        height={height}
                        scrollEnabled={Boolean(height)}
                    >
                        {props.jobDescription}
                    </HtmlContent>
                </HiddenPreview>
            </ScreenDisplay.MobileOnly>
        </>
    );
};
